import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { ArrowRight, X as XIcon, ChevronLeft, ChevronRight } from "lucide-react";

export default function GalleryDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => { api.get(`/galleries/${slug}`).then(r => setG(r.data)).catch(()=>{}); }, [slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox == null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % g.photos.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + g.photos.length) % g.photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, g]);

  if (!g) return <div className="pt-40 text-center text-white/60">Loading...</div>;

  const photos = g.photos || [];

  return (
    <div data-testid="gallery-detail" className="pt-24">
      <SEO title={g.seo_title || g.title} description={g.seo_description || g.description}
           path={`/portfolio/${g.slug}`} image={g.cover_image} />

      {/* Hero */}
      <section className="relative h-[55vh] md:h-[65vh] overflow-hidden">
        {g.cover_image && <img src={g.cover_image} alt={g.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 cine-overlay" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16">
          <Link to="/portfolio" className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-6">← All Galleries</Link>
          <div className="eyebrow">{g.category}</div>
          <h1 className="font-serif text-5xl md:text-7xl font-light mt-3 tracking-tight">{g.title}</h1>
          {g.description && <p className="text-white/70 max-w-2xl mt-4">{g.description}</p>}
          <div className="text-xs text-white/45 mt-4">{photos.length} {photos.length === 1 ? "photograph" : "photographs"}</div>
        </div>
      </section>

      {/* Masonry */}
      <section className="px-3 py-12 max-w-[1800px] mx-auto">
        {photos.length === 0 ? (
          <div className="text-center text-white/40 py-20">No photos in this gallery yet.</div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 [column-fill:_balance]">
            {photos.map((p, idx) => (
              <button key={p.id || idx} onClick={() => setLightbox(idx)}
                data-testid={`gallery-photo-thumb-${idx}`}
                className="mb-3 block w-full break-inside-avoid hover-zoom relative group cursor-pointer">
                <img src={p.url} alt={p.alt || g.title}
                     loading="lazy"
                     className="w-full h-auto object-cover" />
                {p.caption && (
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition">
                    <span className="text-xs text-white/85">{p.caption}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="eyebrow">Inspired?</div>
        <h2 className="font-serif text-4xl mt-3">Let's tell <em className="text-[#D4AF37] not-italic">your story</em>.</h2>
        <div className="flex justify-center gap-3 mt-6">
          <Link to="/contact" className="btn-gold">Say Hello <ArrowRight size={14}/></Link>
          <Link to="/book" className="btn-ghost">Book a Chat</Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox != null && photos[lightbox] && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" data-testid="lightbox" onClick={() => setLightbox(null)}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
                  className="absolute top-6 right-6 text-white/70 hover:text-[#D4AF37] p-2" aria-label="Close" data-testid="lightbox-close">
            <XIcon size={24} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] p-2" aria-label="Previous">
            <ChevronLeft size={32} />
          </button>
          <img src={photos[lightbox].url} alt={photos[lightbox].alt || ""}
               className="max-h-[90vh] max-w-[90vw] object-contain"
               onClick={(e) => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] p-2" aria-label="Next">
            <ChevronRight size={32} />
          </button>
          <div className="absolute bottom-8 inset-x-0 text-center text-white/60 text-sm">
            {photos[lightbox].caption && <div className="mb-1">{photos[lightbox].caption}</div>}
            <div className="text-xs">{lightbox + 1} / {photos.length}</div>
          </div>
        </div>
      )}
    </div>
  );
}
